import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  MiButton,
  MiDatePicker,
  MiDetailFields,
  MiModal,
  MiTimePicker,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { addWorkHours, getDate } from "@miplatform/utils";
import { mdiCalendarBlank, mdiFileDocumentOutline } from "@mdi/js";
import { useCalendarSet } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import { useHoldOrderActionsViewModel } from "./useHoldOrderActionsViewModel";

export const HoldOrderActions = (props) => {
  const {
    id: orderId,
    holdCalendarSetId,
    holdCalendarId,
    modifiedOn,
    onRefresh,
  } = props;
  const theme = useTheme();
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const actionMenuRef = useRef(null);
  const { isLoading, holdOrder } = useHoldOrderActionsViewModel();
  const { calendarSet, getCalendarSet } = useCalendarSet();

  const followUpDefaultOffsetHours = useMemo(
    () =>
      calendarSet?.calendarConfigs?.find(
        (config) => config.id === holdCalendarId
      )?.followUpDefaultOffsetHours || 0,
    [calendarSet, holdCalendarId]
  );

  const edit = useEdit({
    contactName: "",
    reason: "",
    followUpDate: addWorkHours(followUpDefaultOffsetHours).minutes(0),
  });

  useEffect(() => {
    if (isLogModalOpen && holdCalendarSetId) {
      getCalendarSet(holdCalendarSetId);
    }
  }, [isLogModalOpen, holdCalendarSetId]);

  useEffect(() => {
    if (calendarSet) {
      const offsetHours = calendarSet?.followUpDefaultOffsetHours || 0;
      edit.update({
        followUpDate: addWorkHours(offsetHours).minutes(0),
      });
    }
  }, [calendarSet]);

  useEffect(() => {
    edit.update({
      followUpDate: addWorkHours(followUpDefaultOffsetHours).minutes(0),
    });
  }, [followUpDefaultOffsetHours]);

  const holdModalFields = [
    {
      label: "Contact Name",
      name: "contactName",
      required: true,
      width: "100%",
      validationRule: z.string().min(1, "Contact Name is required."),
    },
    {
      label: "Follow-Up Notes",
      name: "reason",
      multiline: true,
      rows: 3,
      width: "100%",
    },
    {
      label: "Next Follow-Up Date",
      name: "followUpDate",
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      width: "170px",
      value: getDate(edit.getValue("followUpDate")),
      customComponent: (
        <MiDatePicker
          error={edit.errors?.followUpDate}
          onChange={(date) => {
            edit.update({
              followUpDate: date,
            });
          }}
          variant={DATEPICKER_VARIANTS.DESKTOP}
          actions={[DATEPICKER_ACTIONS.CANCEL]}
        />
      ),
    },
    {
      name: "followUpTime",
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      width: "170px",
      value: getDate(edit.getValue("followUpDate")),
      customComponent: (
        <MiTimePicker
          shrink
          strictInterval
          timeSteps={{ minutes: 15 }}
          format="hh:mm A"
          label="Follow-Up Time"
          onChange={(time) => {
            edit.update({
              followUpDate: time,
            });
          }}
        />
      ),
    },
  ];
  edit.setValidation(holdModalFields);

  const closeModal = () => {
    edit.reset();
    setIsLogModalOpen(false);
  };

  const handleHold = async () => {
    const response = await holdOrder(orderId, {
      ...edit.getValue(),
      followUpDate: getDate(edit.getValue("followUpDate")),
      calendarId: holdCalendarId,
      modifiedOn,
    });

    if (response) {
      closeModal();
      onRefresh();
    }
  };

  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
      <MiButton
        title="Log Follow-Up"
        startIcon={{
          path: mdiFileDocumentOutline,
          iconSize: 0.8,
          iconColor: theme.palette.grey.medium,
        }}
        onClick={() => setIsLogModalOpen(true)}
        sx={{
          borderColor: theme.palette.grey.medium,
          color: theme.palette.grey.medium,
        }}
        variant={BUTTON_VARIANTS.OUTLINED}
        data-testid="follow-up-btn"
      />
      <MiButton
        title="Schedule"
        sx={{
          borderColor: theme.palette.blue.dark,
          color: theme.palette.blue.dark,
        }}
        startIcon={{
          path: mdiCalendarBlank,
          iconSize: 0.8,
          iconColor: theme.palette.blue.dark,
        }}
        onClick={() => actionMenuRef.current?.openRescheduleModal()}
        variant={BUTTON_VARIANTS.OUTLINED}
        data-testid="schedule-btn"
      />

      <OrdersListActionMenu
        ref={actionMenuRef}
        menuViewConfig={{
          Cancel: true,
          OrderActivityDetails: true,
        }}
        {...props}
      />
      <MiModal
        open={isLogModalOpen}
        closeModal={closeModal}
        width={370}
        titleIcon={mdiFileDocumentOutline}
        leftIconColor={theme.palette.primary.main}
        title="Log Follow-Up"
        loading={isLoading}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: BUTTON_COLORS.BLACK,
            onClick: closeModal,
          },
          {
            name: "Log Follow-Up",
            variant: BUTTON_VARIANTS.CONTAINED,
            disabled:
              !edit.isValid() ||
              !edit.isDirty() ||
              !getDate(edit.getValue("followUpDate"))?.isValid() ||
              isLoading,
            onClick: handleHold,
          },
        ]}
      >
        <MiDetailFields
          edit={edit}
          detailFields={holdModalFields}
          customWrapperStyle={{ gap: 4 }}
        />
      </MiModal>
    </Box>
  );
};
